import { useId } from 'react';
import { Filter, X } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { AutocompleteSelect, Button } from '../../ui';
import {
  ProviderType,
  RequestStatus,
  type UsageFilter,
} from '../../gql/types';
import { PROVIDER_TYPES, providerLabel } from '../shared/providerLabels';

// Filter bar for the usage feed (US-8.5.1). Filters: client / app / key / model / provider / time.
// Provider + status use AutocompleteSelect (PortalDropdown under the hood — zero native <select>,
// DESIGN_LANGUAGE §27). client/app/key ids + canonicalModel are free-text; from/to are date inputs.
//
// The filter is fully controlled by the parent (the page owns it so it can feed BOTH the query
// variables AND the live subscription's filter). `onChange` receives the next filter; empty fields
// are omitted so the variables stay minimal/contract-clean.

const ANY = '__any__';

const STATUS_OPTIONS = [
  { value: ANY, label: 'Any status' },
  { value: RequestStatus.Completed, label: 'Completed' },
  { value: RequestStatus.Failed, label: 'Failed' },
  { value: RequestStatus.Cancelled, label: 'Cancelled' },
];

const PROVIDER_OPTIONS = [
  { value: ANY, label: 'Any provider' },
  ...PROVIDER_TYPES.map((p) => ({ value: p, label: providerLabel(p) })),
];

const PROVIDER_VALUES = new Set<string>(PROVIDER_TYPES);
const STATUS_VALUES = new Set<string>(Object.values(RequestStatus));

export interface UsageFiltersProps {
  value: UsageFilter;
  onChange: (next: UsageFilter) => void;
}

/** Strip empty string / null fields so the emitted filter only carries set values. */
function clean(filter: UsageFilter): UsageFilter {
  const next: UsageFilter = {};
  (Object.entries(filter) as [keyof UsageFilter, unknown][]).forEach(([key, val]) => {
    if (val === '' || val == null) return;
    // @ts-expect-error index assignment across the union is safe here (value already validated).
    next[key] = val;
  });
  return next;
}

export function UsageFilters({ value, onChange }: UsageFiltersProps) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const baseId = useId();

  const set = (patch: Partial<UsageFilter>) => onChange(clean({ ...value, ...patch }));

  const hasAny = Object.values(value).some((v) => v !== '' && v != null);

  const inputClass = `min-h-11 w-full rounded-xl border px-3 py-2.5 text-sm transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/30 ${
    isDark
      ? 'border-white/[0.08] bg-white/[0.04] text-white placeholder-white/30 focus:border-indigo-500'
      : 'border-slate-200 bg-slate-50 text-slate-900 placeholder-slate-400 focus:border-indigo-500'
  }`;
  const labelClass = `mb-1.5 block text-[11px] font-medium uppercase tracking-wider ${
    isDark ? 'text-white/40' : 'text-slate-400'
  }`;

  return (
    <section
      aria-label="Usage filters"
      className={`mb-6 rounded-2xl border p-5 ${
        isDark ? 'border-white/[0.06] bg-white/[0.02]' : 'border-slate-200 bg-white shadow-sm'
      }`}
    >
      <div className="mb-4 flex items-center justify-between">
        <h2
          className={`flex items-center gap-2 text-sm font-semibold ${
            isDark ? 'text-white/70' : 'text-slate-600'
          }`}
        >
          <Filter className="h-4 w-4" aria-hidden="true" />
          Filters
        </h2>
        {hasAny && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onChange({})}
            leftIcon={<X className="h-3.5 w-3.5" aria-hidden="true" />}
          >
            Clear
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div>
          <span className={labelClass}>Provider</span>
          <AutocompleteSelect
            label="Provider"
            value={value.provider ?? ANY}
            options={PROVIDER_OPTIONS}
            onChange={(next) =>
              set({ provider: PROVIDER_VALUES.has(next) ? (next as ProviderType) : null })
            }
          />
        </div>
        <div>
          <span className={labelClass}>Status</span>
          <AutocompleteSelect
            label="Status"
            value={value.status ?? ANY}
            options={STATUS_OPTIONS}
            onChange={(next) =>
              set({ status: STATUS_VALUES.has(next) ? (next as RequestStatus) : null })
            }
          />
        </div>
        <div>
          <label htmlFor={`${baseId}-model`} className={labelClass}>
            Model
          </label>
          <input
            id={`${baseId}-model`}
            type="text"
            value={value.canonicalModel ?? ''}
            onChange={(e) => set({ canonicalModel: e.target.value })}
            placeholder="e.g. gpt-4o"
            className={inputClass}
          />
        </div>
        <div>
          <label htmlFor={`${baseId}-client`} className={labelClass}>
            Client id
          </label>
          <input
            id={`${baseId}-client`}
            type="text"
            value={value.clientId ?? ''}
            onChange={(e) => set({ clientId: e.target.value })}
            placeholder="client_…"
            className={inputClass}
          />
        </div>
        <div>
          <label htmlFor={`${baseId}-app`} className={labelClass}>
            Application id
          </label>
          <input
            id={`${baseId}-app`}
            type="text"
            value={value.applicationId ?? ''}
            onChange={(e) => set({ applicationId: e.target.value })}
            placeholder="app_…"
            className={inputClass}
          />
        </div>
        <div>
          <label htmlFor={`${baseId}-key`} className={labelClass}>
            API key id
          </label>
          <input
            id={`${baseId}-key`}
            type="text"
            value={value.apiKeyId ?? ''}
            onChange={(e) => set({ apiKeyId: e.target.value })}
            placeholder="key_…"
            className={inputClass}
          />
        </div>
        <div>
          <label htmlFor={`${baseId}-from`} className={labelClass}>
            From
          </label>
          <input
            id={`${baseId}-from`}
            type="datetime-local"
            value={value.from ?? ''}
            onChange={(e) => set({ from: e.target.value })}
            className={inputClass}
          />
        </div>
        <div>
          <label htmlFor={`${baseId}-to`} className={labelClass}>
            To
          </label>
          <input
            id={`${baseId}-to`}
            type="datetime-local"
            value={value.to ?? ''}
            onChange={(e) => set({ to: e.target.value })}
            className={inputClass}
          />
        </div>
      </div>
    </section>
  );
}
